import { useState } from "react";
import { AuthPageLayout } from "./AuthPageLayout";
import { useTenantBranding } from "../hooks/useTenantBranding";
import { isDedicatedDomain } from "../loginTenant";
import { LoadingButton } from "./LoadingButton";
import { PasswordField } from "./PasswordField";

/**
 * 通常のログインフォーム UI
 */
export const LoginForm: React.FC<{
  email: string;
  password: string;
  rememberMe: boolean;
  loading: boolean;
  loadingMessage: string;
  error: string | null;
  recoverySuggestion: string | null;
  onEmailChange: (value: string) => void;
  onEmailBlur?: (value: string) => void; // PRODUCT_DEV-396 ログインID blur 時にパスキー利用可否判定 + 自動トリガー
  onPasswordChange: (value: string) => void;
  onRememberMeChange: (value: boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  onForgotPassword: () => void;
  // PRODUCT_DEV-421 alias ログインモード（URL `/:tenantId` 由来。設定されていればラベルを「エイリアス」に切替）
  aliasTenantId?: string | null;
}> = ({
  email,
  password,
  rememberMe,
  loading,
  loadingMessage,
  error,
  recoverySuggestion,
  onEmailChange,
  onEmailBlur,
  onPasswordChange,
  onRememberMeChange,
  onSubmit,
  onForgotPassword,
  aliasTenantId,
}) => {
  const isAliasMode = !!aliasTenantId;
  /**
   * PRODUCT_DEV-641: テナントが**配信ホストで決まっている**か。
   * このときは生の tenantId を画面に出さない（URL とロゴでどこの画面かは分かるので、
   * 内部 ID を顧客に見せる意味が無い）。`/{tenantId}` のパス方式は従来どおり出す。
   */
  const dedicatedDomain = isDedicatedDomain();
  // ロゴと同じ経路。`AuthPageLayout` と同じ tenantId なので取得は 1 本に束ねられる。
  const { loginIdLabel } = useTenantBranding(aliasTenantId ?? null);
  const idPlaceholder = loginIdLabel
    ? `${loginIdLabel}を入力してください`
    : isAliasMode
      ? "エイリアスを入力してください"
      : "ログインIDを入力してください";
  const [showPassword, setShowPassword] = useState(false);
  return (
    // PRODUCT_DEV-641: テナントのロゴを併記するのはログイン画面だけ。
    <AuthPageLayout showTenantBrand>
      {/* ログインタイトル */}
      <h2 className="text-3xl font-bold text-center text-neutral-900 mb-8">
        ログイン
      </h2>

      {/* PRODUCT_DEV-421 alias モード時のテナントID表示（簡易版）。
          PRODUCT_DEV-641: 専用ドメインでは出さない（上記の理由）。 */}
      {isAliasMode && !dedicatedDomain && (
        <p className="text-center text-sm text-neutral-500 mb-4 break-all">
          テナントID: {aliasTenantId}
        </p>
      )}

      {/* ログインフォーム */}
      <form onSubmit={onSubmit} className="space-y-6">
        {/*
          ログインID または エイリアス

          PRODUCT_DEV-641: **alias モードでも onBlur を呼ぶ。** PRODUCT_DEV-421 の時点では
          `handleEmailBlur` がメール前提の `login-options` しか呼ばず、エイリアス入力には
          不適切だったので除外していた。ところが PRODUCT_DEV-484 が
          「まず alias を解決し、解決できたらパスキーを自動起動、できなければメール扱いで
          フォールスルー」を足したのに**除外が残ったまま**になっていた。
          その結果 alias モードでは、`LoginPage` の autofill ポーリング
          （保存済みログインID がある再訪時）からしかパスキーが起動せず、
          **初回ログインの人は登録しても毎回 ID とパスワードを打つ**状態だった。
        */}
        <div>
          <input
            id="login-email"
            type="text"
            placeholder={idPlaceholder}
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            onBlur={(e) => onEmailBlur?.(e.target.value)}
            className="w-full px-6 py-4 border border-neutral-300 rounded-2xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-primary transition-colors bg-white"
            disabled={loading}
          />
        </div>

        {/* パスワード */}
        <PasswordField
          id="login-password"
          name="password"
          autoComplete="current-password"
          placeholder="パスワードを入力してください"
          value={password}
          onChange={onPasswordChange}
          show={showPassword}
          onToggle={() => setShowPassword((v) => !v)}
          disabled={loading}
          required={false}
        />

        {/* 利用規約とプライバシーポリシー */}
        <div className="text-center text-sm text-neutral-700">
          ログインをもって
          <a
            href="https://umeee.co.jp/terms_unizo"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            利用規約
          </a>
          および
          <br />
          <a
            href="https://umeee.co.jp/privacypolicy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            プライバシーポリシー
          </a>
          に同意するものとします
        </div>

        {/* ログインボタン */}
        <LoadingButton loading={loading} loadingMessage={loadingMessage} label="ログイン" />


        {/* エラーメッセージ */}
        {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
        {recoverySuggestion && (
          <div className="mt-1 text-sm text-neutral-700">
            {recoverySuggestion}
          </div>
        )}

        {/* 次回から自動的にログイン */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="rememberMe"
            checked={rememberMe}
            onChange={(e) => onRememberMeChange(e.target.checked)}
            className="w-6 h-6 border-2 border-neutral-300 rounded cursor-pointer"
          />
          <label
            htmlFor="rememberMe"
            className="ml-3 text-neutral-700 cursor-pointer"
          >
            次回から自動的にログイン
          </label>
        </div>

        {/* パスワードを忘れた方 */}
        <div className="text-center">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-primary hover:underline"
            disabled={loading}
          >
            パスワードを忘れた方
          </button>
        </div>
      </form>
    </AuthPageLayout>
  );
};
